import { useEffect, useState } from "react";
import { useFetch } from "../useFetch";
import { getPendingProviderCategories } from "../../core/registration/provider-categories-pending";

type ServiceCategoryOption = {
	id: number;
	label: string;
	subcategories: string[];
};

type SubcategoryLookup = Record<number, Record<string, number>>;
type CategoryOption = {
	id: number;
	name_category: string;
	subcategories_services?: Array<{ id?: number; name_subcategory?: string }>;
};

const extractCategories = (payload: unknown): CategoryOption[] => {
	if (Array.isArray(payload)) return payload as CategoryOption[];
	if (!payload || typeof payload !== "object") return [];
	const maybeWrapped = payload as {
		categories?: CategoryOption[];
		items?: CategoryOption[];
	};
	if (Array.isArray(maybeWrapped.categories)) return maybeWrapped.categories;
	if (Array.isArray(maybeWrapped.items)) return maybeWrapped.items;
	return [];
};

const extractProviderCategoryIds = (payload: unknown): number[] => {
	if (!payload || typeof payload !== "object") return [];

	const record = payload as {
		provider?: { categories_providers?: Array<{ category_id?: number; categories_services?: { id?: number } }> };
		categories_providers?: Array<{ category_id?: number; categories_services?: { id?: number } }>;
		attributes?: {
			categories_providers?: Array<{ category_id?: number; categories_services?: { id?: number } }>;
		};
	};

	const assignments =
		record.categories_providers ??
		record.provider?.categories_providers ??
		record.attributes?.categories_providers ??
		[];

	return Array.from(
		new Set(
			assignments
				.map((assignment) =>
					typeof assignment?.categories_services?.id === "number"
						? assignment.categories_services.id
						: assignment?.category_id,
				)
				.filter(
					(id): id is number =>
						typeof id === "number" && Number.isInteger(id) && id > 0,
				),
		),
	);
};

const normalizeCategory = (value: string) =>
	value
		.toLowerCase()
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "")
		.replace(/[^a-z0-9\s]/g, "")
		.replace(/\s+/g, " ")
		.trim();

export function useProviderServiceCategories(providerId: number | null) {
	const { execute: fetchCategories } = useFetch<unknown>({
		endpoint: "/category",
		method: "GET",
		auto: false,
	});
	const { execute: fetchProvider } = useFetch<unknown>({
		endpoint: "/providers/0",
		method: "GET",
		auto: false,
	});

	const [categoryMap, setCategoryMap] = useState<Record<string, number>>({});
	const [serviceCategories, setServiceCategories] = useState<ServiceCategoryOption[]>(
		[],
	);
	const [subcategoryMapByCategoryId, setSubcategoryMapByCategoryId] =
		useState<SubcategoryLookup>({});
	const [hasLoaded, setHasLoaded] = useState(false);
	const [reloadTick, setReloadTick] = useState(0);

	const reloadCategories = () => {
		setReloadTick((current) => current + 1);
	};

	useEffect(() => {
		let cancelled = false;

		const loadCategories = async () => {
			if (!providerId) {
				if (!cancelled) {
					setCategoryMap({});
					setServiceCategories([]);
					setSubcategoryMapByCategoryId({});
					setHasLoaded(false);
				}
				return;
			}

			if (!cancelled) {
				setHasLoaded(false);
			}

			try {
				const [categoriesPayload, providerPayload] = await Promise.all([
					fetchCategories({ endpoint: "/category" }),
					fetchProvider({ endpoint: `/providers/${providerId}` }).catch(() => null),
				]);

				const categories = extractCategories(categoriesPayload);
				const providerCategoryIds = extractProviderCategoryIds(providerPayload);
				const pendingCategoryIds = getPendingProviderCategories(providerId) ?? [];
				const allowedCategoryIds = new Set(
					[...providerCategoryIds, ...pendingCategoryIds].filter(
						(id) => Number.isInteger(id) && id > 0,
					),
				);
				const filteredCategories =
					allowedCategoryIds.size > 0
						? categories.filter((category) =>
								allowedCategoryIds.has(category.id),
							)
						: [];
				const dedupedCategories = Array.from(
					new Map(
						filteredCategories.map((category) => [
							normalizeCategory(category.name_category ?? ""),
							category,
						]),
					)
						.values(),
				).filter(
					(category) =>
						typeof category?.name_category === "string" &&
						category.name_category.trim().length > 0,
				);

				if (!cancelled) {
					const map = dedupedCategories.reduce<Record<string, number>>(
						(acc, item) => {
							if (item?.name_category) {
								acc[normalizeCategory(item.name_category)] = item.id;
							}
							return acc;
						},
						{},
					);
					const subcategoryLookup = dedupedCategories.reduce<SubcategoryLookup>(
						(acc, item) => {
							const byName = (item.subcategories_services ?? []).reduce<
								Record<string, number>
							>((subAcc, sub) => {
								const subName = sub?.name_subcategory?.trim();
								if (!subName) return subAcc;
								subAcc[normalizeCategory(subName)] =
									typeof (sub as { id?: number }).id === "number"
										? (sub as { id: number }).id
										: 0;
								return subAcc;
							}, {});

							acc[item.id] = Object.fromEntries(
								Object.entries(byName).filter(([, value]) => value > 0),
							);
							return acc;
						},
						{},
					);
					setCategoryMap(map);
					setSubcategoryMapByCategoryId(subcategoryLookup);
					setServiceCategories(
						dedupedCategories.map((item) => ({
							id: item.id,
							label: item.name_category,
							subcategories: Array.isArray(item.subcategories_services)
								? item.subcategories_services
										.map((sub) => sub?.name_subcategory?.trim())
										.filter((sub): sub is string => Boolean(sub))
								: [],
						})),
					);
					setHasLoaded(true);
				}
			} catch {
				if (!cancelled) {
					setCategoryMap({});
					setServiceCategories([]);
					setSubcategoryMapByCategoryId({});
					setHasLoaded(true);
				}
			}
		};

		void loadCategories();
		return () => {
			cancelled = true;
		};
	}, [fetchCategories, fetchProvider, providerId, reloadTick]);

	return {
		categoryMap,
		serviceCategories,
		subcategoryMapByCategoryId,
		hasLoaded,
		reloadCategories,
	};
}